// 5-furlong sprint header — replaces the linear progress bar across the quiz.
// Each answered question advances YOUR horse one furlong along the track.
// Pure CSS animation; no JS timer.

import type { QuizAnswers } from '../types';

interface Props {
  answers: QuizAnswers;
  step: number; // 1..6  (1 = on Q1, 6 = settling)
  totalSteps: number;
  finishing?: boolean; // true during the photo-finish settlement
}

const STYLE_LABEL: Record<string, string> = {
  singles: 'singles bettor',
  accas: 'acca punter',
  in_play: 'in-play bettor',
  each_way: 'each-way racer',
  value_hunting: 'value hunter',
};

const STAKE_LABEL: Record<string, string> = {
  '5-9': 'low stake',
  '10-19': 'mid stake',
  '20-plus': 'big stake',
};

function commentary(step: number, answers: QuizAnswers): string {
  switch (step) {
    case 1:
      return 'Under starter\u2019s orders \u2014 pick your sports.';
    case 2: {
      const n = answers.sports.length;
      return `Out of the gates \u00B7 ${n} sport${n !== 1 ? 's' : ''} backed.`;
    }
    case 3: {
      const style = answers.bettingStyle ? STYLE_LABEL[answers.bettingStyle] : 'style';
      return `Down the back straight \u00B7 ${style} riding fast.`;
    }
    case 4: {
      const stake = answers.stakeBand ? STAKE_LABEL[answers.stakeBand] : 'stake set';
      return `Round the bend \u00B7 ${stake} on the line.`;
    }
    case 5: {
      const f = answers.features.length;
      return `Final furlong \u00B7 ${f ? f + ' must-have' + (f !== 1 ? 's' : '') : 'no must-haves'}.`;
    }
    case 6:
      return 'Photo finish \u2014 weighing in your matches\u2026';
    default:
      return '';
  }
}

export function HorseRace({ answers, step, totalSteps, finishing = false }: Props) {
  const completed = Math.max(0, Math.min(step - 1, totalSteps));
  const progressPct = (completed / totalSteps) * 100;
  const isSettling = step > totalSteps || finishing;

  return (
    <div className={'fb-race' + (isSettling ? ' fb-race--finishing' : '')}>
      <div className="fb-race__topline">
        <span className="fb-race__pill">🏇 5-Furlong Sprint</span>
        <span className="fb-race__progress">
          Furlong {Math.min(completed, totalSteps)} / {totalSteps}
        </span>
      </div>
      <div className="fb-race__track" aria-hidden="true">
        {/* furlong markers (4 internal divisions) */}
        {Array.from({ length: totalSteps - 1 }, (_, i) => (
          <span
            key={i}
            className="fb-race__furlong"
            style={{ left: `${((i + 1) / totalSteps) * 100}%` }}
          />
        ))}
        <span className="fb-race__finish">🏁</span>
        <span
          className={
            'fb-race__horse' +
            (isSettling ? ' fb-race__horse--finishing' : '') +
            (completed === 0 ? ' fb-race__horse--gate' : '')
          }
          style={{ left: `${progressPct}%` }}
        >
          🐎
        </span>
      </div>
      <div className="fb-race__commentary" aria-live="polite">
        {commentary(step, answers)}
      </div>
    </div>
  );
}
